import React from "react"
import { graphql, StaticQuery } from "gatsby"
import { withPreview } from "gatsby-source-prismic-graphql"
import Img from "gatsby-image"
import styled from "styled-components"
import { rem, fluidValue } from "./utils"
import theme from "./theme"
import ExternalLink from "./ExternalLink"

const StyledFooter = styled.footer`
  display: flex;

  background-color: ${({ theme }) => theme.background};
  border-top: 1px solid ${({ theme }) => theme.button.border};
  margin-top: ${rem(32)};
  padding: ${rem(32)} ${rem(24)};

  @media (min-width: ${rem(theme.screens.sm)}) {
    align-items: center;
    justify-content: center;
  }
`

const ContentGroup = styled.div`
  p {
    color: ${({ theme }) => theme.text.body};
    line-height: 150%;
    margin-top: 0;
    margin-bottom: 0.5em;
  }

  @media (min-width: ${rem(theme.screens.sm)}) {
    display: flex;
    align-items: center;

    p {
      margin-bottom: 0;
    }

    a {
      margin: 0;
      white-space: nowrap;
    }
  }
`

const GraphicGroup = styled.div`
  img {
    filter: invert(${({ theme }) => (theme.id === "light" ? 1 : 0)});
  }
`

const query = graphql`
  query {
    file(relativePath: { eq: "footer-graphics.png" }) {
      childImageSharp {
        fixed(width: 110, height: 110) {
          ...GatsbyImageSharpFixed_noBase64
        }
      }
    }
  }
`

const conditionsBlock = {
  title: "Conditions",
  links: [
    {
      text: "Privacy Policy",
      url: "https://lmt.lmt.lv/en/privacy-policy",
    },
    {
      text: "Cookie Settings",
      action: "openCookieSettings",
    },
    {
      text: "Accessibility",
      url: "https://lmt.lmt.lv/en/accessibility",
    },
  ],
}

const StyledFooter2 = styled.footer`
  background-color: ${({ theme }) => theme.background};
  line-height: 130%;
  padding-top: 0;
  padding-bottom: ${rem(32)};
  position: relative;
  z-index: 10;

  @media (min-width: ${rem(theme.screens.md)}) {
    padding-top: 0;
    padding-bottom: ${rem(56)};
  }

  margin-left: auto;
  margin-right: auto;
`

const Container = styled.div`
  max-width: ${rem(1280)};
  margin-right: auto;
  margin-left: auto;
  padding-right: ${rem(24)};
  padding-left: ${rem(24)};
  width: 100%;

  @media (min-width: ${rem(theme.screens.sm)}) {
    padding-right: ${rem(56)};
    padding-left: ${rem(56)};
  }
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 35ch;

  > div + div {
    margin-top: ${rem(32)};
  }

  button {
    appearance: none;
    background: none;
    border: none;
    color: inherit;
    font: inherit;
    cursor: pointer;

    &:hover {
      color: ${theme.colors.white};
      text-decoration: underline;
    }
  }

  a,
  button {
    color: ${theme.colors.white};
    opacity: 0.75;
    text-decoration: none;
    margin: -0.5em;
    padding: 0.5em;
    margin-top: 0.25em;

    &:hover {
      color: ${theme.colors.white};
      text-decoration: underline;
      opacity: 1;
    }
  }

  h4 {
    color: ${({ theme }) => theme.text.heading};
    margin-bottom: 0.75em;
    margin-top: 0;
  }

  @media (min-width: ${rem(theme.screens.md)}) {
    > div + div {
      margin-top: ${rem(56)};
    }
  }
`

export default function Footer({ className, style }) {
  return (
    <>
      <StyledFooter className={className} style={style}>
        <ContentGroup>
          <p>This project is a part of Mobility branch at LMT R&D platform.</p>
          <ExternalLink
            to="https://innovations.lmt.lv/en/"
            label="Check out other products"
          />
        </ContentGroup>

        <GraphicGroup>
          <StaticQuery
            query={query}
            render={withPreview(data => {
              return <Img fixed={data.file.childImageSharp.fixed} />
            }, query)}
          />
        </GraphicGroup>
      </StyledFooter>

      <StyledFooter2>
        <Container>
          <Column>
            <h4>{conditionsBlock.title}</h4>
            {conditionsBlock.links.map((link, index) =>
              link.url ? (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener"
                >
                  {link.text}
                </a>
              ) : (
                <button
                  key={link.action}
                  onClick={() => {
                    if (
                      link.action === "openCookieSettings" &&
                      window &&
                      window.Cookies
                    ) {
                      window.Cookies.show()
                    }
                  }}
                >
                  {link.text}
                </button>
              )
            )}
          </Column>
        </Container>
      </StyledFooter2>
    </>
  )
}
